import {
  IconButton,
  InputAdornment,
  ListItemText,
  Box,
  Autocomplete,
  AutocompleteChangeDetails,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import {
  Dispatch,
  forwardRef,
  SetStateAction,
  SyntheticEvent,
  useState,
} from 'react'
import theme from 'lib/theme'
import { useTranslation } from 'react-i18next'
import {
  OrderSearchIcon,
  SearchField,
  SearchMenuItem,
} from 'components/OrderSearchBar/OrderSearchBar.styled'

type RelatedProductsProps = {
  placeholderText: string
  handleClick: (key: string, query: string) => void
  input: string
  setInput: Dispatch<SetStateAction<string>>
  setQuery: Dispatch<SetStateAction<string | undefined>>
  query: string | undefined
}

const ListBoxComponent = forwardRef<
  HTMLUListElement,
  React.HTMLAttributes<HTMLElement>
>(function ListboxComponent(props, ref) {
  return (
    <ul
      ref={ref}
      {...props}
      role='listbox'
      style={{
        padding: 0,
        maxHeight: 'none',
      }}
    />
  )
})

export const RelatedProductsSearch = ({
  placeholderText,
  handleClick,
  input,
  setInput,
  query,
  setQuery,
}: RelatedProductsProps) => {
  const { t } = useTranslation()
  const [focus, setFocus] = useState(false)
  const [hover, setHover] = useState(false)
  const [iconHover, setIconHover] = useState<string>('')
  const [isOpen, setIsOpen] = useState(false)
  const menuOptions = [
    {
      key: 'gtin',
      label: t('searchContent.gtin'),
    },
    {
      key: 'ext_id',
      label: t('searchContent.extId'),
    },
  ]

  const clearSearch = () => {
    setInput('')
    setQuery(undefined)
  }

  const handleSearch = (
    details: AutocompleteChangeDetails<{ key: string; label: string }>
  ) => {
    handleClick(details.option.key, input)
    setFocus(true)
    setIsOpen(false)
  }
  return (
    <Box height={theme.spacing(8)}>
      <Autocomplete
        disableClearable
        forcePopupIcon={false}
        options={menuOptions}
        id='order'
        open={isOpen}
        role='search'
        value={{ key: '', label: input }}
        onFocus={() => setFocus(true)}
        onBlur={() => setFocus(false)}
        onMouseOver={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        filterOptions={(options) => options}
        sx={{
          marginBottom: theme.spacing(1),
        }}
        onInputChange={(
          event: SyntheticEvent<Element, Event>,
          value: string,
          reason: string
        ) => {
          if (reason === 'input') {
            setInput(value)
            if (value.length > 2) {
              setIsOpen(true)
            } else {
              setIsOpen(false)
            }
          }
        }}
        onChange={(e, value, reason, details) => {
          if (reason === 'selectOption') {
            if (details) {
              handleSearch(details)
            }
          }
        }}
        disablePortal
        freeSolo
        ListboxComponent={ListBoxComponent}
        renderOption={(props, option) => (
          <SearchMenuItem
            {...props}
            width='100%'
            key={option.key}
            aria-hidden
            onMouseOver={() => setIconHover(option.key)}
            onMouseLeave={() => setIconHover('')}
            iconHover={iconHover === option.key}
            value={input}>
            <ListItemText>
              <b>{option.label}</b> {input}
            </ListItemText>
            <OrderSearchIcon aria-hidden hover={iconHover === option.key} />
          </SearchMenuItem>
        )}
        noOptionsText={t('noOptionsAutoComplete')}
        renderInput={(params) => (
          <SearchField
            {...params}
            backgroundColor={
              query
                ? theme.palette.commonColors.white
                : theme.palette.secondary.main
            }
            helperText={input.length < 2 && focus ? t('enterCharacters') : ''}
            placeholder={placeholderText}
            InputProps={{
              ...params.InputProps,
              ...(!focus &&
                !query && {
                  startAdornment: (
                    <InputAdornment position='start'>
                      <OrderSearchIcon aria-hidden hover={hover} />
                    </InputAdornment>
                  ),
                }),
              ...((query || focus) && {
                endAdornment: (
                  <InputAdornment position='end'>
                    <IconButton
                      onMouseDown={clearSearch}
                      aria-label={t('clearSearch')}
                      sx={{
                        '&:hover': {
                          cursor: 'pointer',
                          background: 'transparent',
                          color: theme.palette.action.hover,
                        },
                      }}>
                      <CloseIcon
                        sx={{
                          '&:hover': {
                            background: 'transparent',
                          },
                        }}
                      />
                    </IconButton>
                  </InputAdornment>
                ),
              }),
            }}
          />
        )}
      />
    </Box>
  )
}
